/** Absence and open cover requirements table for the Labour Management demo screen. */

import { Button, Chip, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AbsenceCoverRow } from '../types';

interface AbsenceCoverTableProps {
  rows: AbsenceCoverRow[];
  onFindCover: (row: AbsenceCoverRow) => void;
}

const COVER_STATUS_COLOR: Record<AbsenceCoverRow['coverStatus'], 'success' | 'warning' | 'error'> = {
  Covered: 'success',
  'Seeking cover': 'warning',
  Uncovered: 'error',
};

const AbsenceCoverTable = ({ rows, onFindCover }: AbsenceCoverTableProps) => {
  const { t } = useTranslation('common');

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('labourManagementEmployeeRole', 'Employee / role')}</TableCell>
            <TableCell>{t('labourManagementSite', 'Site')}</TableCell>
            <TableCell>{t('labourManagementShift', 'Shift')}</TableCell>
            <TableCell>{t('labourManagementAbsenceReason', 'Absence reason')}</TableCell>
            <TableCell>{t('labourManagementCoverStatus', 'Cover status')}</TableCell>
            <TableCell>{t('labourManagementImpact', 'Impact')}</TableCell>
            <TableCell align="right">{t('labourManagementActions', 'Actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={7}>{t('labourManagementNoAbsences', 'No absences recorded for the current filters.')}</TableCell></TableRow>
          )}
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>
                <Typography variant="textSM"
                  fontWeight={600}>{row.employee}</Typography>
                <Typography variant="textXS"
                  color="text.secondary">{row.role}</Typography>
              </TableCell>
              <TableCell>{row.site}</TableCell>
              <TableCell>{row.shift}</TableCell>
              <TableCell>{row.reason}</TableCell>
              <TableCell>
                <Chip label={t(`labourManagementCoverStatus${row.coverStatus.replace(' ', '')}`, row.coverStatus)}
                  color={COVER_STATUS_COLOR[row.coverStatus]}
                  size="small"
                  variant="outlined" />
              </TableCell>
              <TableCell>{row.impact}</TableCell>
              <TableCell align="right">
                <Stack direction="row"
                  spacing={1}
                  justifyContent="flex-end">
                  <Button variant="secondary"
                    size="md"
                    disabled={row.coverStatus === 'Covered'}
                    onClick={() => onFindCover(row)}>
                    {t('labourManagementFindCover', 'Find cover')}
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AbsenceCoverTable;
